import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useSchool } from '../../../contexts/SchoolContext';
import { useAuth } from '../../../features/auth/hooks/useAuth';
import { GmailService } from '../../../services/communication/GmailService';

export default function EmailThreadView({ threadId, studentId, onBack, onSendAsChat }) {
  const { schoolId } = useSchool();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all messages in this thread
  useEffect(() => {
    if (!schoolId || !threadId) return;

    const colRef = collection(db, 'schools', schoolId, 'communications');
    const q = query(
      colRef,
      where('gmailThreadId', '==', threadId)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const aTime = a.receivedAt?.toMillis?.() || 0;
            const bTime = b.receivedAt?.toMillis?.() || 0;
            return aTime - bTime;
          });
        setMessages(docs);
        setLoading(false);

        // Mark unread messages as read
        docs
          .filter(m => !m.isRead && m.direction === 'inbound')
          .forEach(m => GmailService.markAsRead(m.id, schoolId).catch(() => {}));
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [schoolId, threadId]);

  // Derive reply metadata from the latest inbound message
  const latestInbound = [...messages].reverse().find(m => m.direction === 'inbound');
  const threadSubject = messages[0]?.subject || '(no subject)';

  const handleSend = async () => {
    if (!replyBody.trim()) return;
    setSending(true);
    setError(null);
    try {
      await GmailService.sendReply({
        gmailThreadId: threadId,
        toEmail: latestInbound?.fromEmail || '',
        subject: `Re: ${threadSubject}`,
        body: replyBody,
        studentId,
        parentId: latestInbound?.parentId || null,
        schoolId,
      });
      setReplyBody('');
    } catch (err) {
      setError(err.message || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-base-300">
        <button className="btn btn-ghost btn-sm btn-square" onClick={onBack}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{threadSubject}</p>
          {latestInbound && (
            <p className="text-xs text-base-content/50 truncate">
              {latestInbound.fromName || latestInbound.fromEmail}
              {latestInbound.studentName ? ` · ${latestInbound.studentName}` : ''}
            </p>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <span className="loading loading-spinner loading-md" />
          </div>
        ) : messages.length === 0 ? (
          <p className="text-sm text-base-content/50 text-center py-12">No messages in this thread.</p>
        ) : (
          messages.map((msg) => {
            const isOutbound = msg.direction === 'outbound';
            return (
              <div
                key={msg.id}
                className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-4 py-3 ${
                    isOutbound
                      ? 'bg-primary/10 border border-primary/20'
                      : 'bg-base-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-base-content/70">
                      {isOutbound ? 'You' : (msg.fromName || msg.fromEmail)}
                    </span>
                    <span className="text-[10px] text-base-content/40">
                      {formatDate(msg.receivedAt)}
                    </span>
                  </div>
                  <div className="text-sm text-base-content/90 whitespace-pre-wrap break-words">
                    {msg.body || msg.snippet || '(no content)'}
                  </div>
                  {msg.attachments?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {msg.attachments.map((att, i) => (
                        <span key={i} className="badge badge-ghost badge-xs gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          {att.filename}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Reply input */}
      <div className="p-4 border-t border-base-300 space-y-2">
        {error && <div className="alert alert-error text-xs py-2">{error}</div>}
        <textarea
          className="textarea textarea-bordered w-full text-sm"
          rows={3}
          placeholder={latestInbound ? `Reply to ${latestInbound.fromName || latestInbound.fromEmail}...` : 'Write a reply...'}
          value={replyBody}
          onChange={(e) => setReplyBody(e.target.value)}
          disabled={sending}
        />
        <div className="flex justify-between items-center">
          {onSendAsChat && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => onSendAsChat(replyBody, studentId)}
              disabled={!replyBody.trim()}
            >
              Send as Chat
            </button>
          )}
          <button
            className="btn btn-primary btn-sm ml-auto"
            onClick={handleSend}
            disabled={!replyBody.trim() || sending}
          >
            {sending ? <span className="loading loading-spinner loading-xs" /> : null}
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
